import { ReactNode, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-hot-toast';
import S from './HWConfigurationStyles';
import useGetFleetId from '../../../../API/hooks/useGetFleetId';
import useGetDeviceConfigurationPerFleet from '../../../../API/hooks/devices/useGetDeviceConfigurationPerFleet';
import useGetDeviceDefaultConfiguration from '../../../../API/hooks/devices/useGetDeviceDefaultConfiguration';
import useGetDeviceDefaultConfigFile from '../../../../API/hooks/devices/useGetDeviceDefaultConfigFile';
import { DEFAULTS_UNAVAILABLE_MESSAGE, PRESERVED_CONFIG_KEY_LABELS, resolveDefaultHint } from '../../../../constants/DeviceConfig';
import {
  DeviceConfigurationFields, DeviceConfigurationResetResult, DeviceTestConfiguration,
} from '../../../../API/types/Devices';
import { Button } from '../../../../components/ui/Button/Button';
import CheckBox from '../../../../components/common/CheckBox/CheckBox';
import { alertEventsMap } from '../../../Alerts/constants/alertEventsMap';
import WatchVideo from '../../../../components/common/LiveVideo/LiveVideo';
import {
  speedLimitValidator,
  distanceValidation,
  precisionValidator2,
  degreeValidatior,
  precisionValidator,
  vehicleDistanceCameraToFrontOfVehicleValidation,
  vehicleLateralCameraOffsetValidation,
  haExtrinsicsCameraOffsetZValidation,
  rotationValidatior,
  precisionValidator4,
  vehicleLengthValidation,
  vehicleWidthValidation,
  normalizeDecimal,
  validateDecimalField,
  loadFormikValuesWithValidation,
  validateBlockageColumnWidthField,
  validateBlockageColumnWidthsSum,
} from '../DeviceSettings/helpers';
import useGetDeviceConfiguration from '../../../../API/hooks/devices/useGetDeviceConfiguration';
import usePatchDeviceConfiguration from '../../../../API/hooks/devices/usePatchDeviceConfiguration';
import usePostDeviceConfigurationResetDefault from '../../../../API/hooks/devices/usePostDeviceConfigurationResetDefault';
import useGetDeviceTestConfiguration from '../../../../API/hooks/devices/useGetDeviceTestConfiguration';
import usePostDeviceTestConfiguration from '../../../../API/hooks/devices/usePostDeviceTestConfiguration';
import Modal from '../../../../components/common/Modal/Modal';
import InfoModal from '../../../../components/common/InfoModal/InfoModal';
import ConfirmModal from '../../../../components/common/ConfirmModal/ConfirmModal';
import { FeaturesManagement } from '../../../../featuresControl';
import { objectIsEmpty } from '../../../../utils/commonUtils';
import Input from '../../../../components/common/Input/Input';
import useGetDeviceLiveVideoIsEnabled from '../../../../API/hooks/devices/useGetDeviceLiveVideoIsEnabled';
import useGetFleetFeatures from '../../../../API/hooks/useGetFleetFeatures';

type Props = {
  toggleModal: (closed: boolean) => void;
  deviceId: string;
  isOnline: boolean;
};
const eventsEnableList1 = [
  'config/IMU/hard_brake/', // DevStreamEventHardBrake alert
  'config/IMU/hard_acceleration/', // DevStreamEventHardAcceleration
  'config/IMU/hard_turn/', // DevStreamEventHardTurn
  'config/IMU/high_g/', // DevStreamEventHighG
  'config/WIRE-CUT/wire_cut/', // DevStreamPowerCUt
];

const eventsEnableList2 = [
  'config/ICDP/drowsiness/', // DevStreamEventDrowsiness
  'config/ICDP/eyes_of_the_road/', // DevStreamEventEyesOffRoad
  'config/ICDP/phone_usage/', // DevStreamEventMobilePhoneUsage
  'config/ECDP/front_collision_warning/', // DevStreamEventFcw
  'config/ECDP/pedestrian_collision_warning/', // DevStreamEventPcw
  'config/ECDP/lane_departure_warning/', // DevStreamEventLdw
  'config/ECDP/speed_limit_warning/', // DevStreamEventOverspeed
  'config/ECDP/tailgating_warning/', // DevStreamEventTailgaiting
  'config/ICDP/anti_spoofing/', // DevStreamDriverNotFound
  'config/ICDP/camera_blockage/', // DevStreamEventCameraBlockageInterior
  'config/ECDP/camera_blockage/', // DevStreamEventCameraBlockageExterior
];

const eventsActiveWarningList = [
  'config/IMU/hard_brake/', // DevStreamEventHardBrake ActiveWarning
  'config/IMU/hard_acceleration/', // DevStreamEventHardAcceleration ActiveWarning
  'config/IMU/hard_turn/', // DevStreamEventHardTurn ActiveWarning
  'config/ICDP/drowsiness/', // DevStreamEventDrowsiness ActiveWarning
  'config/ICDP/eyes_of_the_road/', // DevStreamEventEyesOffRoad ActiveWarning
  'config/ICDP/phone_usage/', // DevStreamEventEyesOffRoad ActiveWarning
  'config/ECDP/front_collision_warning/', // DevStreamEventFcw ActiveWarning
  'config/ECDP/pedestrian_collision_warning/', // DevStreamEventPcw ActiveWarning
  'config/ECDP/lane_departure_warning/', // DevStreamEventLdw ActiveWarning
  'config/ECDP/speed_limit_warning/', // DevStreamEventOverspeed ActiveWarning
  'config/WIRE-CUT/wire_cut/', // DevStreamEventPowerSupplyCut
  'config/ECDP/tailgating_warning/', // DevStreamEventTailgaiting ActiveWarning
];

function HWConfiguration({ toggleModal, deviceId, isOnline }: Props): JSX.Element {
  const [deviceOfflineModalOpen, toggleDeviceOfflineSaveModal] = useState(false);
  const [confirmSaveModalOpen, toggleConfirmSaveModal] = useState(false);
  const [liveVideoModalOpen, toggleLiveVideoModal] = useState(false);
  const [resetDefaultModalOpen, toggleResetDefaultModal] = useState(false);
  const [testConfigModalOpen, toggleTestConfigModal] = useState(false);
  const [infoModalOpen, toggleInfoModal] = useState(false);
  const [infoModalContent, setInfoModalContent] = useState<ReactNode>('');
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const offlineNote = 'Device is offline, changes will be applied after device become online!';
  const showInfoModal = (message: string): void => {
    setInfoModalContent(isOnline ? message : `${message} ${offlineNote}`);
    toggleInfoModal(true);
  };
  const onSubmitSuccess = (): any => {
    if (isOnline) {
      toggleConfirmSaveModal(true);
      // toast.success('Configuration was saved');
    } else {
      toggleDeviceOfflineSaveModal(true);
    }
  };
  const onSubmitFail = (errorMessage: string): any => {
    toast.error(`Failed to save settings. Error: ${errorMessage}`);
    setSubmitError(errorMessage);
  };
  // A reset is not a save, and its most common failure — no config file uploaded for this
  // device's HW model and FW version — carries a message that tells the user what to do about
  // it, so it gets its own handler rather than being labelled "Failed to save settings".
  const onResetDefaultFail = (errorMessage: string): any => {
    toast.error(`Failed to reset configuration. ${errorMessage}`);
    setSubmitError(errorMessage);
  };
  const fleetId = useGetFleetId();
  const { data: fleetConfig, isLoading: fleetIsLoading } = useGetDeviceConfigurationPerFleet(fleetId);
  const { data: deviceConfig, isLoading: deviceIsLoading, error: deviceError } = useGetDeviceConfiguration(deviceId, fleetId);
  const { data: defaultConfig, isLoading: isLoadingDefaults } = useGetDeviceDefaultConfiguration();
  const isLoadingConfig = deviceIsLoading || isLoadingDefaults;
  // Covers every parameter, so it is what the form is seeded and enumerated from.
  const defaults = defaultConfig?.configuration;
  // What "Reset to Default" would actually write back for this device. An uploaded file need not
  // carry every parameter, so it labels fields but never seeds them.
  const { data: defaultConfigFile } = useGetDeviceDefaultConfigFile(deviceId, fleetId);
  const resetDefaults = defaultConfigFile?.configuration;
  const defaultConfigFileMissing = defaultConfigFile ? !defaultConfigFile.available : false;
  const defaultHint = (key: keyof DeviceConfigurationFields): string => resolveDefaultHint(key, resetDefaults, defaults);
  const { mutate } = usePatchDeviceConfiguration(onSubmitSuccess, onSubmitFail);
  const onResetDefaultSuccess = (result: DeviceConfigurationResetResult): void => {
    setSubmitError(null);
    const preservedKeys = result.preservedKeys ?? [];
    setInfoModalContent(
      <S.InfoContent>
        <p>Device was reset to the default configuration for its HW model and FW version.</p>
        {!isOnline && <p>{offlineNote}</p>}
        {!!preservedKeys.length && (
          <>
            <p className="preserved-note">
              Camera calibration and vehicle dimensions were not reset — they describe how this
              device is mounted in this vehicle. They can only be reset from the admin portal.
            </p>
            <div className="preserved-list">
              {preservedKeys.map((key) => (
                <div key={key}>{PRESERVED_CONFIG_KEY_LABELS[key] ?? key}</div>
              ))}
            </div>
          </>
        )}
      </S.InfoContent>,
    );
    toggleInfoModal(true);
  };
  const onTestConfigSuccess = (result: DeviceTestConfiguration): void => {
    setSubmitError(null);
    showInfoModal(result.enabled
      ? 'Test config enabled. Activation speed of all alerts was set to 0 km/h.'
      : 'Test config disabled. Changed parameters were reverted to their previous values.');
  };
  const {
    data: testConfig, isLoading: testConfigIsLoading,
  } = useGetDeviceTestConfiguration(deviceId, fleetId);
  const {
    mutate: resetToDefault, isLoading: resetIsSaving,
  } = usePostDeviceConfigurationResetDefault(onResetDefaultSuccess, onResetDefaultFail);
  const {
    mutate: applyTestConfig, isLoading: testConfigIsSaving,
  } = usePostDeviceTestConfiguration(onTestConfigSuccess, onSubmitFail);
  const testConfigEnabled = testConfig?.enabled ?? false;
  const [allEnableChecked, setAllEnableChecked] = useState(false);
  const [allActiveWarningChecked, setAllActiveWarningChecked] = useState(false);
  const [fleetIsActive, setFleetIsActive] = useState(fleetConfig?.isActive !== undefined ? fleetConfig.isActive : true);
  const videoStreamEnable = useGetDeviceLiveVideoIsEnabled(deviceId, fleetId);
  const { videoRecording: videoRecordingEnabled, liveVideo: liveVideoEnabled } = useGetFleetFeatures();
  const noDeviceConfig = (deviceError && (deviceError as any).response.status === 404) || !deviceConfig?.configuration;
  const deviceConfigNotReady = (deviceConfig?.configuration && objectIsEmpty(deviceConfig.configuration) && (deviceConfig.status === 'INIT' || deviceConfig.status === 'FAILED')) || false;
  // Both cloud-config actions write straight to the device config, so they need one to exist first.
  const configActionsDisabled = deviceIsLoading || fleetIsLoading || (noDeviceConfig as boolean)
    || deviceConfigNotReady || resetIsSaving || testConfigIsSaving;

  const formik = useFormik({
    // Seeded from the service defaults once they arrive — see the effect below. The form is not
    // rendered until then, so it is never shown with these empty values.
    initialValues: {} as DeviceConfigurationFields,
    onSubmit: (values) => {
      mutate({
        fleetId,
        deviceId,
        configuration: Object.fromEntries(
          Object.entries(values).map(([k, v]) => [k, typeof v === 'string' ? normalizeDecimal(v) : v]),
        ) as DeviceConfigurationFields,
      });
    },
    validate: (values) => {
      const errors = {} as Record<string, string>;

      // validateOnMount fires on the first render, before the fetched defaults have seeded the
      // form, and the checks below read their key unconditionally. Nothing to validate until then.
      if (!Object.keys(values).length) return errors;

      if (values['config/ECDP/speed_limit_warning/threshold_speed'] !== undefined && !speedLimitValidator.test(values['config/ECDP/speed_limit_warning/threshold_speed'].toString())) {
        errors['config/ECDP/speed_limit_warning/threshold_speed'] = 'Can contain only numbers in range from 5 to 200 km/h';
      }

      if (!values['config/E3D/zone_calibration/m4'].toString().length) {
        errors['config/E3D/zone_calibration/m4'] = 'Required';
      }

      if (!values['config/E3D/zone_calibration/m3'].toString().length) {
        errors['config/E3D/zone_calibration/m3'] = 'Required';
      }

      if (!values['config/E3D/zone_calibration/m2'].toString().length) {
        errors['config/E3D/zone_calibration/m2'] = 'Required';
      }

      if (!values['config/E3D/zone_calibration/m1'].toString().length) {
        errors['config/E3D/zone_calibration/m1'] = 'Required';
      }

      if (!values['config/E3D/zone_calibration/pitch'].toString().length) {
        errors['config/E3D/zone_calibration/pitch'] = 'Required';
      }

      if (!values['config/E3D/zone_calibration/yaw'].toString().length) {
        errors['config/E3D/zone_calibration/yaw'] = 'Required';
      }

      if (!values['config/E3D/zone_calibration/d'].toString().length) {
        errors['config/E3D/zone_calibration/d'] = 'Required';
      }

      if (!values['config/E3D/zone_calibration/blockage_leftColumnWidth'].toString().length) {
        errors['config/E3D/zone_calibration/blockage_leftColumnWidth'] = 'Required';
      }

      if (!values['config/E3D/zone_calibration/blockage_rightColumnWidth'].toString().length) {
        errors['config/E3D/zone_calibration/blockage_rightColumnWidth'] = 'Required';
      }

      if (!values['config/HA/extrinsics/camera_offset_x'].toString().length) {
        errors['config/HA/extrinsics/camera_offset_x'] = 'Required';
      }

      if (!values['config/HA/extrinsics/camera_offset_y'].toString().length) {
        errors['config/HA/extrinsics/camera_offset_y'] = 'Required';
      }

      if (!values['config/HA/extrinsics/camera_offset_z'].toString().length) {
        errors['config/HA/extrinsics/camera_offset_z'] = 'Required';
      }

      if (!values['config/HA/extrinsics/camera_yaw'].toString().length) {
        errors['config/HA/extrinsics/camera_yaw'] = 'Required';
      }

      if (!values['config/HA/extrinsics/camera_pitch'].toString().length) {
        errors['config/HA/extrinsics/camera_pitch'] = 'Required';
      }

      if (!values['config/HA/extrinsics/camera_roll'].toString().length) {
        errors['config/HA/extrinsics/camera_roll'] = 'Required';
      }

      if (!values['config/HA/general/vehicle_length'].toString().length) {
        errors['config/HA/general/vehicle_length'] = 'Required';
      }

      if (!values['config/HA/general/vehicle_width'].toString().length) {
        errors['config/HA/general/vehicle_width'] = 'Required';
      }

      if (values['config/E3D/zone_calibration/m4']) {
        const e = validateDecimalField(values['config/E3D/zone_calibration/m4'].toString(), distanceValidation, 'Can contain only numbers in range from 0 up to 2', precisionValidator2); if (e) errors['config/E3D/zone_calibration/m4'] = e;
      }
      if (values['config/E3D/zone_calibration/m3']) {
        const e = validateDecimalField(values['config/E3D/zone_calibration/m3'].toString(), distanceValidation, 'Can contain only numbers in range from 0 up to 2', precisionValidator2); if (e) errors['config/E3D/zone_calibration/m3'] = e;
      }
      if (values['config/E3D/zone_calibration/m2']) {
        const e = validateDecimalField(values['config/E3D/zone_calibration/m2'].toString(), distanceValidation, 'Can contain only numbers in range from 0 up to 2', precisionValidator2); if (e) errors['config/E3D/zone_calibration/m2'] = e;
      }
      if (values['config/E3D/zone_calibration/m1']) {
        const e = validateDecimalField(values['config/E3D/zone_calibration/m1'].toString(), distanceValidation, 'Can contain only numbers in range from 0 up to 2', precisionValidator2); if (e) errors['config/E3D/zone_calibration/m1'] = e;
      }
      if (values['config/E3D/zone_calibration/pitch']) {
        const e = validateDecimalField(values['config/E3D/zone_calibration/pitch'].toString(), degreeValidatior, 'Can contain only numbers in range from -50 up to 50', precisionValidator); if (e) errors['config/E3D/zone_calibration/pitch'] = e;
      }
      if (values['config/E3D/zone_calibration/yaw']) {
        const e = validateDecimalField(values['config/E3D/zone_calibration/yaw'].toString(), degreeValidatior, 'Can contain only numbers in range from -50 up to 50', precisionValidator); if (e) errors['config/E3D/zone_calibration/yaw'] = e;
      }
      if (values['config/E3D/zone_calibration/d']) {
        const e = validateDecimalField(values['config/E3D/zone_calibration/d'].toString(), distanceValidation, 'Can contain only numbers in range from 0 up to 2', precisionValidator2); if (e) errors['config/E3D/zone_calibration/d'] = e;
      }
      if (values['config/E3D/zone_calibration/blockage_leftColumnWidth']) {
        const e = validateBlockageColumnWidthField(values['config/E3D/zone_calibration/blockage_leftColumnWidth'].toString()); if (e) errors['config/E3D/zone_calibration/blockage_leftColumnWidth'] = e;
      }
      if (values['config/E3D/zone_calibration/blockage_rightColumnWidth']) {
        const e = validateBlockageColumnWidthField(values['config/E3D/zone_calibration/blockage_rightColumnWidth'].toString()); if (e) errors['config/E3D/zone_calibration/blockage_rightColumnWidth'] = e;
      }
      const blockageSumError = validateBlockageColumnWidthsSum(
        values['config/E3D/zone_calibration/blockage_leftColumnWidth'],
        values['config/E3D/zone_calibration/blockage_rightColumnWidth'],
      );
      if (blockageSumError) {
        errors['config/E3D/zone_calibration/blockage_rightColumnWidth'] = blockageSumError;
      }
      if (values['config/HA/extrinsics/camera_offset_x']) {
        const e = validateDecimalField(values['config/HA/extrinsics/camera_offset_x'].toString(), vehicleDistanceCameraToFrontOfVehicleValidation, 'Only numbers in range from -4 up to 4', precisionValidator2); if (e) errors['config/HA/extrinsics/camera_offset_x'] = e;
      }
      if (values['config/HA/extrinsics/camera_offset_y']) {
        const e = validateDecimalField(values['config/HA/extrinsics/camera_offset_y'].toString(), vehicleLateralCameraOffsetValidation, 'Only numbers in range from -2 up to 2', precisionValidator2); if (e) errors['config/HA/extrinsics/camera_offset_y'] = e;
      }
      if (values['config/HA/extrinsics/camera_offset_z']) {
        const e = validateDecimalField(values['config/HA/extrinsics/camera_offset_z'].toString(), haExtrinsicsCameraOffsetZValidation, 'Only numbers in range from 0 up to 4', precisionValidator2); if (e) errors['config/HA/extrinsics/camera_offset_z'] = e;
      }
      if (values['config/HA/extrinsics/camera_pitch']) {
        const e = validateDecimalField(values['config/HA/extrinsics/camera_pitch'].toString(), rotationValidatior, 'Only numbers in range from -360 up to 360', precisionValidator4); if (e) errors['config/HA/extrinsics/camera_pitch'] = e;
      }
      if (values['config/HA/extrinsics/camera_yaw']) {
        const e = validateDecimalField(values['config/HA/extrinsics/camera_yaw'].toString(), rotationValidatior, 'Only numbers in range from -360 up to 360', precisionValidator4); if (e) errors['config/HA/extrinsics/camera_yaw'] = e;
      }
      if (values['config/HA/extrinsics/camera_roll']) {
        const e = validateDecimalField(values['config/HA/extrinsics/camera_roll'].toString(), rotationValidatior, 'Only numbers in range from -360 up to 360', precisionValidator4); if (e) errors['config/HA/extrinsics/camera_roll'] = e;
      }
      if (values['config/HA/general/vehicle_length']) {
        const e = validateDecimalField(values['config/HA/general/vehicle_length'].toString(), vehicleLengthValidation, 'Only numbers in range from 0 up to 30', precisionValidator2); if (e) errors['config/HA/general/vehicle_length'] = e;
      }
      if (values['config/HA/general/vehicle_width']) {
        const e = validateDecimalField(values['config/HA/general/vehicle_width'].toString(), vehicleWidthValidation, 'Only numbers in range from 0 up to 5', precisionValidator2); if (e) errors['config/HA/general/vehicle_width'] = e;
      }

      return errors;
    },
    validateOnMount: true,
  });

  // Writes the service defaults, fetched rather than held locally — the portal used to send its own
  // copy here, which had drifted five parameters behind what the service actually defaults to.
  const createDefaultConfig = (): void => {
    if (!defaults) return;
    mutate({
      fleetId,
      deviceId,
      configuration: defaults,
    });
  };

  // `formik.values` starts empty and is seeded asynchronously by the effect below. Every field
  // reads `formik.values[...].toString()` unconditionally, so rendering before it is populated
  // throws during render and unmounts the whole app — the form waits for this instead.
  const valuesReady = Object.keys(formik.values).length > 0;

  const checkAllSelected = (type: 'activeWarning' | 'enable'): boolean => {
    if (type === 'activeWarning') {
      return eventsActiveWarningList.every((n) => formik.values[`${n}active_warning` as keyof typeof formik.values] !== false);
    }
    return eventsEnableList1.every((n) => formik.values[`${n}enabled` as keyof typeof formik.values] !== false) && eventsEnableList2.every((n) => formik.values[`${n}enable` as keyof typeof formik.values] !== false);
  };

  useEffect(() => {
    setFleetIsActive(fleetConfig?.isActive !== undefined ? fleetConfig.isActive : true);
  }, [fleetConfig]);

  const handleCheckboxChange = (id: string, state: boolean): void => {
    formik.setFieldValue(id, state);
    formik.setSubmitting(false);
    setSubmitError('');
  };

  const handleAllCheckboxChange = (id: string, state: boolean): void => {
    if (id === 'enable') {
      setAllEnableChecked(state);
      eventsEnableList1.forEach((n: string) => formik.setFieldValue(`${n}enabled`, state));
      eventsEnableList2.forEach((n: string) => formik.setFieldValue(`${n}enable`, state));
    } else if (id === 'activeWarning') {
      setAllActiveWarningChecked(state);
      eventsActiveWarningList.forEach((n: string) => formik.setFieldValue(`${n}active_warning`, state));
    }
    formik.setSubmitting(false);
    setSubmitError('');
  };

  const handleInputChange = (e: any): void => {
    const { name, value } = e.target;
    formik.setFieldValue(name, value, true);
    formik.setFieldTouched(name, true, false);
    formik.setSubmitting(false);
    setSubmitError('');
  };

  useEffect(() => {
    formik.setSubmitting(false);
    if (formik.values) {
      setAllEnableChecked(checkAllSelected('enable'));
      setAllActiveWarningChecked(checkAllSelected('activeWarning'));
    }
  }, [formik.values, deviceConfig]);

  useEffect(() => {
    if (!deviceConfig?.configuration || !defaults) return;
    const filteredDeviceConfig = Object.fromEntries(Object.entries(deviceConfig.configuration).filter(([key]) => Object.keys(defaults).includes(key)));
    const updatedConfig = { ...defaults, ...filteredDeviceConfig };
    loadFormikValuesWithValidation(formik, updatedConfig as DeviceConfigurationFields)
      .catch((error) => console.error('Failed to validate loaded form values', error));
  }, [deviceConfig, defaults]);

  useEffect(() => {
    formik.setSubmitting(false);
  }, [submitError, formik.values]);

  return (
    <S.Wrapper>
      <form onSubmit={formik.handleSubmit}>
        <div className="device-configuration">
          {isLoadingConfig && (<div className="loading">Configuration is loading...</div>)}
          {(!isLoadingConfig && !defaults) && (
            <p className="warning">{DEFAULTS_UNAVAILABLE_MESSAGE}</p>
          )}
          {(!isLoadingConfig && noDeviceConfig as boolean) && (
            <div>
              <p className="warning">No device configuration</p>
              <S.Row>
                {FeaturesManagement.tools.defaultCreator && <Button type="submit" onClick={createDefaultConfig}>Create default config</Button>}
              </S.Row>
            </div>
          )}
          {(!isLoadingConfig && deviceConfigNotReady) && (
            <p className="warning">Device configuration is not available. Let's wait device apply last changes.</p>
          )}
          {(!isLoadingConfig && !!defaults && valuesReady
            && !deviceConfigNotReady && !noDeviceConfig) && (
            <>
              <S.Row>
                <p> </p>
                <CheckBox
                  label="Select all"
                  name="enable"
                  state={allEnableChecked}
                  onChange={handleAllCheckboxChange}
                  disabled={deviceIsLoading || fleetIsActive}
                />
                <CheckBox
                  label="Select all"
                  name="activeWarning"
                  state={allActiveWarningChecked}
                  onChange={handleAllCheckboxChange}
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <h4>Events:</h4>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventFcw.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ECDP/front_collision_warning/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/front_collision_warning/enable"
                  disabled={deviceIsLoading || fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ECDP/front_collision_warning/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/front_collision_warning/active_warning"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventPcw.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ECDP/pedestrian_collision_warning/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/pedestrian_collision_warning/enable"
                  disabled={deviceIsLoading || fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ECDP/pedestrian_collision_warning/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/pedestrian_collision_warning/active_warning"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventLdw.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ECDP/lane_departure_warning/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/lane_departure_warning/enable"
                  disabled={deviceIsLoading || fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ECDP/lane_departure_warning/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/lane_departure_warning/active_warning"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventOverspeed.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ECDP/speed_limit_warning/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/speed_limit_warning/enable"
                  disabled={deviceIsLoading || fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ECDP/speed_limit_warning/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/speed_limit_warning/active_warning"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventWireCut.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/WIRE-CUT/wire_cut/enabled']}
                  onChange={handleCheckboxChange}
                  name="config/WIRE-CUT/wire_cut/enabled"
                  disabled={deviceIsLoading || fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/WIRE-CUT/wire_cut/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/WIRE-CUT/wire_cut/active_warning"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventTailgaiting.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ECDP/tailgating_warning/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/tailgating_warning/enable"
                  disabled={deviceIsLoading || fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ECDP/tailgating_warning/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/tailgating_warning/active_warning"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventDrowsiness.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ICDP/drowsiness/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/drowsiness/enable"
                  disabled={deviceIsLoading || fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ICDP/drowsiness/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/drowsiness/active_warning"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventEyesOffRoad.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ICDP/eyes_of_the_road/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/eyes_of_the_road/enable"
                  disabled={deviceIsLoading || fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ICDP/eyes_of_the_road/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/eyes_of_the_road/active_warning"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventCameraBlockageInterior.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ICDP/camera_blockage/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/camera_blockage/enable"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventCameraBlockageExterior.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ECDP/camera_blockage/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ECDP/camera_blockage/enable"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventMobilePhoneUsage.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ICDP/phone_usage/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/phone_usage/enable"
                  disabled={deviceIsLoading || fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/ICDP/phone_usage/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/phone_usage/active_warning"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventHighG.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/IMU/high_g/enabled']}
                  onChange={handleCheckboxChange}
                  name="config/IMU/high_g/enabled"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventHardBrake.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/IMU/hard_brake/enabled']}
                  onChange={handleCheckboxChange}
                  name="config/IMU/hard_brake/enabled"
                  disabled={deviceIsLoading || fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/IMU/hard_brake/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/IMU/hard_brake/active_warning"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventHardAcceleration.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/IMU/hard_acceleration/enabled']}
                  onChange={handleCheckboxChange}
                  name="config/IMU/hard_acceleration/enabled"
                  disabled={deviceIsLoading || fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/IMU/hard_acceleration/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/IMU/hard_acceleration/active_warning"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamEventHardTurn.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/IMU/hard_turn/enabled']}
                  onChange={handleCheckboxChange}
                  name="config/IMU/hard_turn/enabled"
                  disabled={deviceIsLoading || fleetIsActive}
                />
                <CheckBox
                  label="Sound"
                  state={formik.values['config/IMU/hard_turn/active_warning']}
                  onChange={handleCheckboxChange}
                  name="config/IMU/hard_turn/active_warning"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <S.Row>
                <p>{alertEventsMap.DevStreamDriverNotFound.shortDesc}:</p>
                <CheckBox
                  label="Enabled"
                  state={formik.values['config/ICDP/anti_spoofing/enable']}
                  onChange={handleCheckboxChange}
                  name="config/ICDP/anti_spoofing/enable"
                  disabled={deviceIsLoading || fleetIsActive}
                />
              </S.Row>
              <div className="divider"> </div>
              <h4>Common configurations:</h4>
              <S.RowContainer>
                <div className="input-component">
                  <CheckBox
                    label="GPS enabled"
                    state={formik.values['config/GPS/localization/enable']}
                    onChange={handleCheckboxChange}
                    name="config/GPS/localization/enable"
                    disabled={fleetIsLoading || deviceIsLoading || fleetIsActive}
                  />
                </div>
                <div className="input-component">
                  <CheckBox
                    label="Inward camera video recording enabled"
                    state={formik.values['config/Video/recording/enabled_icam']}
                    onChange={handleCheckboxChange}
                    name="config/Video/recording/enabled_icam"
                    disabled={fleetIsLoading || deviceIsLoading || !videoRecordingEnabled || fleetIsActive}
                  />
                </div>
              </S.RowContainer>
              <S.RowContainer>
                <div className="input-component">
                  <CheckBox
                    label="Driver identification enabled"
                    state={formik.values['config/E3D/driver_id/enable']}
                    onChange={handleCheckboxChange}
                    name="config/E3D/driver_id/enable"
                    disabled={fleetIsLoading || deviceIsLoading || fleetIsActive}
                  />
                </div>
                <div className="input-component">
                  <CheckBox
                    label="Outward camera video recording enabled"
                    state={formik.values['config/Video/recording/enabled_ecam']}
                    onChange={handleCheckboxChange}
                    name="config/Video/recording/enabled_ecam"
                    disabled={fleetIsLoading || deviceIsLoading || !videoRecordingEnabled || fleetIsActive}
                  />
                </div>
              </S.RowContainer>
              <S.RowContainer>
                <p className={!videoRecordingEnabled ? 'message warning' : 'hidden'}>
                  Warning! Video recording is disabled. Please contact your administractor to turn on.
                </p>
              </S.RowContainer>
              <S.RowContainer>
                <p className="groupName">Events threshold configurations:</p>
              </S.RowContainer>
              <S.RowContainer>
                <div className="input-component">
                  <Input
                    label="Vehicle Speed Limit (km/h)"
                    key="config/ECDP/speed_limit_warning/threshold_speed"
                    name="config/ECDP/speed_limit_warning/threshold_speed"
                    placeholder="Type value from 5 to 200"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/ECDP/speed_limit_warning/threshold_speed')}
                    value={formik.values['config/ECDP/speed_limit_warning/threshold_speed'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading || fleetIsActive}
                    error={formik.touched['config/ECDP/speed_limit_warning/threshold_speed'] && formik.errors['config/ECDP/speed_limit_warning/threshold_speed']}
                  />
                </div>
              </S.RowContainer>
              <h4>Interior Camera Calibration:</h4>
              <S.RowContainer>
                <div className="input-component">
                  <Input
                    label="Distance to the top edge of the windshield (m)"
                    key="config/E3D/zone_calibration/m4"
                    name="config/E3D/zone_calibration/m4"
                    placeholder="0 up to 2"
                    step={0.01}
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/E3D/zone_calibration/m4')}
                    value={formik.values['config/E3D/zone_calibration/m4'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/E3D/zone_calibration/m4'] && formik.errors['config/E3D/zone_calibration/m4']}
                  />
                </div>
                <div className="input-component">
                  <Input
                    label="Distance to the bottom edge of the windshield (m)"
                    key="config/E3D/zone_calibration/m3"
                    name="config/E3D/zone_calibration/m3"
                    placeholder="0 up to 2"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/E3D/zone_calibration/m3')}
                    value={formik.values['config/E3D/zone_calibration/m3'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/E3D/zone_calibration/m3'] && formik.errors['config/E3D/zone_calibration/m3']}
                  />
                </div>
              </S.RowContainer>
              <S.RowContainer>
                <div className="input-component">
                  <Input
                    label="Distance to the right edge of the windshield (from veh. driver viewpoint)"
                    key="config/E3D/zone_calibration/m2"
                    name="config/E3D/zone_calibration/m2"
                    placeholder="0 up to 2"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/E3D/zone_calibration/m2')}
                    value={formik.values['config/E3D/zone_calibration/m2'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/E3D/zone_calibration/m2'] && formik.errors['config/E3D/zone_calibration/m2']}
                  />
                </div>
                <div className="input-component">
                  <Input
                    label="Distance to the left edge of the windshield (from veh. driver viewpoint)"
                    key="config/E3D/zone_calibration/m1"
                    name="config/E3D/zone_calibration/m1"
                    placeholder="0 up to 2"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/E3D/zone_calibration/m1')}
                    value={formik.values['config/E3D/zone_calibration/m1'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/E3D/zone_calibration/m1'] && formik.errors['config/E3D/zone_calibration/m1']}
                  />
                </div>
              </S.RowContainer>
              <S.RowContainer>
                <div className="input-component">
                  <Input
                    label="Camera pitch angle (deg)"
                    key="config/E3D/zone_calibration/pitch"
                    name="config/E3D/zone_calibration/pitch"
                    placeholder="-50 up to +50"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/E3D/zone_calibration/pitch')}
                    value={formik.values['config/E3D/zone_calibration/pitch'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/E3D/zone_calibration/pitch'] && formik.errors['config/E3D/zone_calibration/pitch']}
                  />
                </div>
                <div className="input-component">
                  <Input
                    label="Camera yaw angle (deg)"
                    key="config/E3D/zone_calibration/yaw"
                    name="config/E3D/zone_calibration/yaw"
                    placeholder="-50 up to +50"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/E3D/zone_calibration/yaw')}
                    value={formik.values['config/E3D/zone_calibration/yaw'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/E3D/zone_calibration/yaw'] && formik.errors['config/E3D/zone_calibration/yaw']}
                  />
                </div>
              </S.RowContainer>
              <S.RowContainer>
                <div className="input-component">
                  <Input
                    label="Absolute distance from camera to the closest point on the windshield"
                    key="config/E3D/zone_calibration/d"
                    name="config/E3D/zone_calibration/d"
                    placeholder="0 up to 2"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/E3D/zone_calibration/d')}
                    value={formik.values['config/E3D/zone_calibration/d'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/E3D/zone_calibration/d'] && formik.errors['config/E3D/zone_calibration/d']}
                  />
                </div>
              </S.RowContainer>
              <S.RowContainer>
                <div className="input-component">
                  <Input
                    label="Interior camera blockage region width (left)"
                    key="config/E3D/zone_calibration/blockage_leftColumnWidth"
                    name="config/E3D/zone_calibration/blockage_leftColumnWidth"
                    placeholder="0 up to 1600"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/E3D/zone_calibration/blockage_leftColumnWidth')}
                    value={formik.values['config/E3D/zone_calibration/blockage_leftColumnWidth'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/E3D/zone_calibration/blockage_leftColumnWidth'] && formik.errors['config/E3D/zone_calibration/blockage_leftColumnWidth']}
                  />
                </div>
                <div className="input-component">
                  <Input
                    label="Interior camera blockage region width (right)"
                    key="config/E3D/zone_calibration/blockage_rightColumnWidth"
                    name="config/E3D/zone_calibration/blockage_rightColumnWidth"
                    placeholder="0 up to 1600"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/E3D/zone_calibration/blockage_rightColumnWidth')}
                    value={formik.values['config/E3D/zone_calibration/blockage_rightColumnWidth'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/E3D/zone_calibration/blockage_rightColumnWidth'] && formik.errors['config/E3D/zone_calibration/blockage_rightColumnWidth']}
                  />
                </div>
              </S.RowContainer>
              <h4>Exterior Camera Calibration:</h4>
              <S.RowContainer>
                <div className="input-component">
                  <Input
                    label="Camera offset X (m)"
                    key="config/HA/extrinsics/camera_offset_x"
                    name="config/HA/extrinsics/camera_offset_x"
                    placeholder="-4 up to 4"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/HA/extrinsics/camera_offset_x')}
                    value={formik.values['config/HA/extrinsics/camera_offset_x'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/HA/extrinsics/camera_offset_x'] && formik.errors['config/HA/extrinsics/camera_offset_x']}
                  />
                </div>
                <div className="input-component">
                  <Input
                    label="Camera offset Y (m)"
                    key="config/HA/extrinsics/camera_offset_y"
                    name="config/HA/extrinsics/camera_offset_y"
                    placeholder="-2 up to 2"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/HA/extrinsics/camera_offset_y')}
                    value={formik.values['config/HA/extrinsics/camera_offset_y'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/HA/extrinsics/camera_offset_y'] && formik.errors['config/HA/extrinsics/camera_offset_y']}
                  />
                </div>
                <div className="input-component">
                  <Input
                    label="Camera offset Z (m)"
                    key="config/HA/extrinsics/camera_offset_z"
                    name="config/HA/extrinsics/camera_offset_z"
                    placeholder="0 up to 4"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/HA/extrinsics/camera_offset_z')}
                    value={formik.values['config/HA/extrinsics/camera_offset_z'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/HA/extrinsics/camera_offset_z'] && formik.errors['config/HA/extrinsics/camera_offset_z']}
                  />
                </div>
              </S.RowContainer>
              <S.RowContainer>
                <div className="input-component">
                  <Input
                    label="Yaw rotation of exterior camera (deg)"
                    key="config/HA/extrinsics/camera_yaw"
                    name="config/HA/extrinsics/camera_yaw"
                    placeholder="-360 up to 360"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/HA/extrinsics/camera_yaw')}
                    value={formik.values['config/HA/extrinsics/camera_yaw'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/HA/extrinsics/camera_yaw'] && formik.errors['config/HA/extrinsics/camera_yaw']}
                  />
                </div>
                <div className="input-component">
                  <Input
                    label="Pitch rotation of exterior camera (deg)"
                    key="config/HA/extrinsics/camera_pitch"
                    name="config/HA/extrinsics/camera_pitch"
                    placeholder="-360 up to 360"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/HA/extrinsics/camera_pitch')}
                    value={formik.values['config/HA/extrinsics/camera_pitch'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/HA/extrinsics/camera_pitch'] && formik.errors['config/HA/extrinsics/camera_pitch']}
                  />
                </div>
                <div className="input-component">
                  <Input
                    label="Roll rotation of exterior camera (deg)"
                    key="config/HA/extrinsics/camera_roll"
                    name="config/HA/extrinsics/camera_roll"
                    placeholder="-360 up to 360"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/HA/extrinsics/camera_roll')}
                    value={formik.values['config/HA/extrinsics/camera_roll'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/HA/extrinsics/camera_roll'] && formik.errors['config/HA/extrinsics/camera_roll']}
                  />
                </div>
              </S.RowContainer>
              <h4>Vehicle spec:</h4>
              <S.RowContainer>
                <div className="input-component">
                  <Input
                    label="Vehicle length (m)"
                    key="config/HA/general/vehicle_length"
                    name="config/HA/general/vehicle_length"
                    placeholder="0 up to 30"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/HA/general/vehicle_length')}
                    value={formik.values['config/HA/general/vehicle_length'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/HA/general/vehicle_length'] && formik.errors['config/HA/general/vehicle_length']}
                  />
                </div>
                <div className="input-component">
                  <Input
                    label="Vehicle width (m)"
                    key="config/HA/general/vehicle_width"
                    name="config/HA/general/vehicle_width"
                    placeholder="0 up to 5"
                    onChange={handleInputChange}
                    onBlur={formik.handleBlur}
                    defaultValue={defaultHint('config/HA/general/vehicle_width')}
                    value={formik.values['config/HA/general/vehicle_width'].toString()}
                    disabled={fleetIsLoading || deviceIsLoading}
                    error={formik.touched['config/HA/general/vehicle_width'] && formik.errors['config/HA/general/vehicle_width']}
                  />
                </div>
              </S.RowContainer>
              {fleetIsActive && (
                <>
                  <div className="divider"> </div>
                  <S.Row>
                    <div className="warning">Global fleet settings have been applied. Only device-specific fields can be edited.</div>
                  </S.Row>
                  <S.RowContainer className={submitError ? '' : 'hidden'}>
                    <p className="message error">Submitting error: {typeof submitError === 'string' ? submitError : submitError?.map((error) => error)}</p>
                  </S.RowContainer>
                </>
              )}
            </>
          )}
        </div>
        <S.Buttons>
          <Button
            height="50px"
            onClick={() => toggleModal(false)}
            type="button"
            className="close-button"
          >
            Close
          </Button>
          <Button
            height="50px"
            width="160px"
            onClick={() => toggleResetDefaultModal(true)}
            type="button"
            title={defaultConfigFileMissing
              ? 'No default configuration is available for this device. Contact support to have it uploaded.'
              : 'Reset the device to the default configuration for its HW model and FW version'}
            disabled={configActionsDisabled || defaultConfigFileMissing}
          >
            Reset to Default
          </Button>
          <Button
            height="50px"
            width="160px"
            onClick={() => toggleTestConfigModal(true)}
            type="button"
            active={testConfigEnabled}
            title="Set the activation speed of all alerts to 0 km/h so they can be triggered on a standing vehicle"
            disabled={configActionsDisabled || testConfigIsLoading}
          >
            {testConfigEnabled ? 'Disable Test Config' : 'Enable Test Config'}
          </Button>
          <Button
            height="50px"
            // eslint-disable-next-line no-nested-ternary
            title={(!liveVideoEnabled || !videoStreamEnable) ? 'Video streaming is disabled.\nPlease contact your administractor to turn on.' : (isOnline ? 'Show video' : 'Device is offline')}
            onClick={() => toggleLiveVideoModal(true)}
            type="button"
            disabled={!isOnline || !liveVideoEnabled || !videoStreamEnable}
          >
            Show Live Video
          </Button>
          <Button
            className="save-button"
            height="50px"
            type="submit"
            disabled={formik.isSubmitting || deviceIsLoading || fleetIsLoading || noDeviceConfig as boolean || deviceConfigNotReady as boolean || deviceError as boolean || !formik.isValid}
          >
            Save
          </Button>

        </S.Buttons>
      </form>
      {confirmSaveModalOpen && (
        <Modal
          title="Device settings"
          state={confirmSaveModalOpen}
          toggleState={toggleConfirmSaveModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <InfoModal
            title="New settings were submitted!"
            onClose={() => toggleConfirmSaveModal(false)}
            btnConfirmText="OK"
          />
        </Modal>
      )}
      {deviceOfflineModalOpen && (
        <Modal
          title="Device configuration"
          state={deviceOfflineModalOpen}
          toggleState={toggleDeviceOfflineSaveModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <InfoModal
            title="Device is offline. Changes will be applied after device become online!"
            onClose={() => toggleDeviceOfflineSaveModal(false)}
            btnConfirmText="OK"
          />
        </Modal>
      )}
      {resetDefaultModalOpen && (
        <Modal
          title="Reset configuration"
          state={resetDefaultModalOpen}
          toggleState={toggleResetDefaultModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <ConfirmModal
            title={defaultConfigFileMissing
              ? `No default configuration is available for this device (HW model ${defaultConfigFile?.hwModel || 'unknown'}, FW version ${defaultConfigFile?.fwVersion || 'unknown'}), so there is nothing to reset to. Contact support to have it uploaded.`
              : 'Reset this device to the default configuration for its HW model and FW version? Unsaved changes in this form will be lost.'}
            btnConfirmText="Reset"
            onClose={() => toggleResetDefaultModal(false)}
            onConfirm={() => {
              toggleResetDefaultModal(false);
              resetToDefault({ fleetId, deviceId });
            }}
          />
        </Modal>
      )}
      {testConfigModalOpen && (
        <Modal
          title="Test config"
          state={testConfigModalOpen}
          toggleState={toggleTestConfigModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <ConfirmModal
            title={testConfigEnabled
              ? 'Disable the test config? All parameters changed by it will be reverted to their previous values.'
              : 'Enable the test config? The activation speed of all alerts will be set to 0 km/h until you disable it.'}
            btnConfirmText={testConfigEnabled ? 'Disable' : 'Enable'}
            onClose={() => toggleTestConfigModal(false)}
            onConfirm={() => {
              toggleTestConfigModal(false);
              applyTestConfig({ fleetId, deviceId, enabled: !testConfigEnabled });
            }}
          />
        </Modal>
      )}
      {infoModalOpen && (
        <Modal
          title="Device configuration"
          state={infoModalOpen}
          toggleState={toggleInfoModal}
          settings={{
            minWidth: '500px',
          }}
        >
          <InfoModal
            title={infoModalContent}
            onClose={() => toggleInfoModal(false)}
            btnConfirmText="OK"
          />
        </Modal>
      )}
      {liveVideoModalOpen && (
        <Modal
          title="Video stream"
          state={liveVideoModalOpen}
          toggleState={toggleLiveVideoModal}
        >
          <WatchVideo
            deviceId={deviceId}
          />
        </Modal>
      )}
    </S.Wrapper>
  );
}

export default HWConfiguration;
